import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { getOfficePhoneLinesSummaryDetails } from '../../vulcanPricing/officePhone/getOfficePhoneLinesSummaryDetails';
import { getOfficePhoneLinesTotals } from '../../vulcanPricing/officePhone/officePhone';
import { TableWrapper, Table, Th, Td, TermRow } from '../businessOptikTv/css';

/**
 * Office Phone Lines Table Component
 * Displays all added phone lines in a table format
 */
const OfficePhoneLinesTable = ({
  officePhoneLinesData = [],
  onEditLine,
  onDeleteLine,
  isSummary = false
}) => {
  if (!officePhoneLinesData || officePhoneLinesData.length === 0) {
    return null;
  }

  const getNumberOfLines = lineData => {
    const numberOfLines = get(lineData, 'numberOfLines');
    const parsed = parseInt(numberOfLines, 10);

    // If numberOfLines is not provided, empty, or invalid, default to 1
    if (!numberOfLines || Number.isNaN(parsed) || parsed < 1) {
      return 1;
    }

    return parsed;
  };

  const calculateLineTotal = lineData => {
    const summaryDetails = getOfficePhoneLinesSummaryDetails(lineData);
    let total = 0;

    summaryDetails.forEach(row => {
      total += get(row, 'monthlyCharges', 0);
    });

    return total;
  };

  const formatCallingFeatures = lineData => {
    const features = [];

    // 1 Feature (for Office Phone Basic)
    const callingFeature1 = get(lineData, 'callingFeature1');
    if (callingFeature1) {
      features.push(`1 Feature - ${callingFeature1}`);
    }

    // Unlimited features (for Office Phone and Office Phone Plus)
    const callingFeatureUnlimited = get(
      lineData,
      'callingFeatureUnlimited',
      []
    );
    if (callingFeatureUnlimited && callingFeatureUnlimited.length > 0) {
      features.push(`${callingFeatureUnlimited.join(', ')}`);
    }

    return features.join(', ') || '';
  };

  const formatAddOns = lineData => {
    const addOns = get(lineData, 'addOns', []);
    return addOns.join(', ') || '';
  };

  const getTotals = () => {
    return getOfficePhoneLinesTotals(officePhoneLinesData);
  };

  const handleEdit = lineData => {
    if (onEditLine) {
      onEditLine(lineData);
    }
  };

  const handleDelete = lineId => {
    if (onDeleteLine) {
      onDeleteLine(lineId);
    }
  };

  return (
    <div className="office-phone-lines-table">
      <TableWrapper>
        <Table style={{ minWidth: '1500px' }}>
          <thead>
            <tr>
              <Th width="110px">Order Type</Th>
              <Th width="80px">Term</Th>
              <Th width="110px">Plan</Th>
              <Th width="120px">Customer Type</Th>
              <Th width="130px">Line Designation</Th>
              <Th width="110px">Line Type</Th>
              <Th width="90px"># of Lines</Th>
              <Th width="140px">Phone Number(s)</Th>
              <Th width="140px">Calling Features</Th>
              <Th width="120px">Toll Free Number</Th>
              <Th width="130px">Add Ons</Th>
              <Th width="120px">Monthly Charges</Th>
              <Th width="130px">One-Time Charges</Th>
              {!isSummary && (
                <Th width="100px" style={{ minWidth: '100px' }}>
                  Actions
                </Th>
              )}
            </tr>
          </thead>
          <tbody>
            {officePhoneLinesData.map((lineData, index) => (
              <TermRow key={get(lineData, 'id', index)}>
                <Td>{get(lineData, 'orderType', '')}</Td>
                <Td>{get(lineData, 'term', '')}</Td>
                <Td>{get(lineData, 'plan', '')}</Td>
                <Td>{get(lineData, 'customerType', '')}</Td>
                <Td>{get(lineData, 'lineDesignation', '')}</Td>
                <Td>{get(lineData, 'lineType', '')}</Td>
                <Td>
                  {getNumberOfLines(lineData) > 1
                    ? getNumberOfLines(lineData).toString()
                    : ''}
                </Td>
                <Td>{get(lineData, 'phoneNumbers', '')}</Td>
                <Td>{formatCallingFeatures(lineData)}</Td>
                <Td>{get(lineData, 'tollFreeNumber', '')}</Td>
                <Td>{formatAddOns(lineData)}</Td>
                <Td>${calculateLineTotal(lineData).toFixed(2)}</Td>
                <Td>$0.00</Td>
                {!isSummary && (
                  <Td>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        alignItems: 'center'
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => handleEdit(lineData)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#2B8000',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          width: '100%',
                          minWidth: '70px'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(get(lineData, 'id', index))}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#d32f2f',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          width: '100%',
                          minWidth: '70px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </Td>
                )}
              </TermRow>
            ))}
            {officePhoneLinesData.length > 0 && (
              <TermRow>
                <Td
                  colSpan="11"
                  style={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    color: '#333'
                  }}
                >
                  Total
                </Td>
                <Td
                  style={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    color: '#333'
                  }}
                >
                  ${getTotals().monthly.toFixed(2)}
                </Td>
                <Td
                  style={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    color: '#333'
                  }}
                >
                  ${getTotals().oneTime.toFixed(2)}
                </Td>
                {!isSummary && <Td style={{ backgroundColor: '#f5f5f5' }}></Td>}
              </TermRow>
            )}
          </tbody>
        </Table>
      </TableWrapper>
    </div>
  );
};

OfficePhoneLinesTable.propTypes = {
  officePhoneLinesData: PropTypes.arrayOf(PropTypes.shape({})),
  onEditLine: PropTypes.func,
  onDeleteLine: PropTypes.func,
  isSummary: PropTypes.bool
};

export default OfficePhoneLinesTable;
